/**
 * LanguageSwitcher — globe icon + menu in the AppRail (I18N_PLAN.md 0.2).
 *
 * The plan drafted this as a MUI Select in Layout.tsx; Layout was since
 * consolidated into the 64px icon-only AppRail, so the rail idiom applies:
 * an IconButton that opens a Menu (same pattern as the account menu).
 *
 * Selection goes through i18n.changeLanguage — the languagedetector's
 * localStorage cache persists it for anonymous visitors — and, when
 * signed in, is also PATCHed to the user profile so the backend can
 * localize AI output and error messages (resolution: user.locale →
 * Accept-Language → "en").
 *
 * Language names are endonyms (each language in itself) — identical in
 * every locale's JSON by design, so they stay readable mid-switch.
 * The pseudo-locale entry only exists in development builds.
 */

import React, { useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslation } from "react-i18next";

import { PSEUDO_LOCALE } from "../i18n";
import { updateMyLocale } from "../api/client";
import { useAuth } from "../hooks/useAuth";

interface LanguageSwitcherProps {
  /** Tooltip/menu placement, mirroring the rail's responsive axis. */
  placement: "bottom" | "right";
}

export default function LanguageSwitcher({ placement }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation("common");
  const { isAuthenticated } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const languages = [
    { code: "en", label: t("LanguageSwitcher.english") },
    { code: "es", label: t("LanguageSwitcher.spanish") },
    ...(process.env.NODE_ENV === "development"
      ? [{ code: PSEUDO_LOCALE, label: t("LanguageSwitcher.pseudo") }]
      : []),
  ];

  const selectLanguage = (code: string) => {
    setAnchor(null);
    void i18n.changeLanguage(code); // languagedetector caches to localStorage
    if (isAuthenticated) {
      // Fire-and-forget: a failed preference write must never block the UI
      // switch. Backend endpoint ships with plan task 0.5.
      updateMyLocale(code).catch(() => undefined);
    }
  };

  return (
    <>
      <Tooltip title={t("LanguageSwitcher.tooltip")} placement={placement}>
        <IconButton
          onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          aria-label={t("LanguageSwitcher.tooltip") ?? undefined}
          aria-haspopup="menu"
          sx={{
            width: 40,
            height: 40,
            color: "text.secondary",
            flexShrink: 0,
            "&:hover": { color: "text.primary", bgcolor: "action.hover" },
          }}
        >
          <LanguageIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={
          placement === "bottom"
            ? { vertical: "bottom", horizontal: "right" }
            : { vertical: "center", horizontal: "right" }
        }
        transformOrigin={
          placement === "bottom"
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "center", horizontal: "left" }
        }
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        {languages.map(({ code, label }) => (
          <MenuItem
            key={code}
            selected={i18n.resolvedLanguage === code || i18n.language === code}
            onClick={() => selectLanguage(code)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
